// Imports
import React from 'react';
import Bowser from 'bowser';
import styled from 'styled-components/macro';
import theme from '../../../../theme';
import { getContrast, setLightness, setSaturation } from 'polished';


// Styled component
const Wrapper = styled.div`
	padding: 1rem 1.25rem;
	line-height: 1rem;
	
	color: ${getContrast(theme.colors.danger, '#fff') > 1.9
		? '#fff'
		: setSaturation(0.2, setLightness(0.3, theme.colors.danger))};
	
	background-color: ${theme.colors.danger};
	
	position: sticky;
	top: 0;
	left: 0;
	z-index: 10;
`;


// Define the accepted props
interface Props {
	minimumVersions?: {
		chrome?: number;
		edge?: number;
		firefox?: number;
		safari?: number;
		opera?: number;
		chromeForAndroid?: number;
		firefoxForAndroid?: number;
		safariOnIOS?: number;
		samsungInternet?: number;
		operaMobile?: number;
		androidBrowser?: number;
	};
}


// Function component
const BrowserVersionWarningBanner: React.FC<Props> = ({ minimumVersions: minimumVersionsProp }) => {
	// Minimum required versions by shareable components within Spark
	const sparkMinimumVersions = {
		chrome: 80, // Supports `overflow-wrap: anywhere`
		edge: 80, // Supports `overflow-wrap: anywhere`
		firefox: 93, // Supports input attribute `type=datetime-local`
		safari: 15.4, // Supports `overflow-wrap: anywhere`
		opera: 67, // Supports `overflow-wrap: anywhere`
		chromeForAndroid: 132, // Supports `overflow-wrap: anywhere`
		firefoxForAndroid: 132, // Supports `overflow-wrap: anywhere`
		safariOnIOS: 15.4, // Supports `overflow-wrap: anywhere`
		samsungInternet: 13, // Supports `overflow-wrap: anywhere`
		operaMobile: 80, // Supports `overflow-wrap: anywhere`
		androidBrowser: 132, // Supports `overflow-wrap: anywhere`
	};
	
	
	// Build minimum versions by comparing Spark requirements with prop requirements
	const minimumVersions = {
		chrome: Math.max(minimumVersionsProp?.chrome ?? 0, sparkMinimumVersions.chrome),
		edge: Math.max(minimumVersionsProp?.edge ?? 0, sparkMinimumVersions.edge),
		firefox: Math.max(minimumVersionsProp?.firefox ?? 0, sparkMinimumVersions.firefox),
		safari: Math.max(minimumVersionsProp?.safari ?? 0, sparkMinimumVersions.safari),
		opera: Math.max(minimumVersionsProp?.opera ?? 0, sparkMinimumVersions.opera),
		chromeForAndroid: Math.max(minimumVersionsProp?.chromeForAndroid ?? 0, sparkMinimumVersions.chromeForAndroid),
		firefoxForAndroid: Math.max(minimumVersionsProp?.firefoxForAndroid ?? 0, sparkMinimumVersions.firefoxForAndroid),
		safariOnIOS: Math.max(minimumVersionsProp?.safariOnIOS ?? 0, sparkMinimumVersions.safariOnIOS),
		samsungInternet: Math.max(minimumVersionsProp?.samsungInternet ?? 0, sparkMinimumVersions.samsungInternet),
		operaMobile: Math.max(minimumVersionsProp?.operaMobile ?? 0, sparkMinimumVersions.operaMobile),
		androidBrowser: Math.max(minimumVersionsProp?.androidBrowser ?? 0, sparkMinimumVersions.androidBrowser),
	};
	
	
	// Initialize parser
	const browser = Bowser.getParser(window.navigator.userAgent);
	
	
	// Check if browser satisfies version requirements
	//  -> Ignores unrecognized browsers
	const satisfiesMinimumVersionRequirements = browser.satisfies({
		opera: `>=${minimumVersions.opera}`,
		Android: {
			chrome: `>=${minimumVersions.chromeForAndroid}`,
			firefox: `>=${minimumVersions.firefoxForAndroid}`,
		},
		iOS: {
			safari: `>=${minimumVersions.safariOnIOS}`,
		},
		samsung_internet: `>=${minimumVersions.samsungInternet}`,
		mobile: {
			opera: `>=${minimumVersions.operaMobile}`,
		},
		android: `>=${minimumVersions.androidBrowser}`,
	});
	
	if (satisfiesMinimumVersionRequirements !== false) {
		return null;
	}
	
	
	// Return JSX
	return (
		<Wrapper color='danger'>
			It looks like you’re using{' '}
			<strong>
				{browser.getBrowserName()} {browser.getBrowserVersion()}
			</strong>
			. We recommend upgrading to the latest version of {browser.getBrowserName()}, or you may experience unexpected
			behavior while using this site.
		</Wrapper>
	);
};

export default BrowserVersionWarningBanner;
